import React from "react";
import "@/App.css";
import "@/lib/i18n";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import InstallPWA from "./components/InstallPWA";
import ForcePasswordChange from "./components/ForcePasswordChange";
import AppUpdateBanner from "./components/AppUpdateBanner";

// Pages
import LoginPage from "./pages/LoginPage";
import UpgradeRequired from "./pages/UpgradeRequired";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import PilotManagement from "./pages/admin/PilotManagement";
import VehicleManagement from "./pages/admin/VehicleManagement";
import SupplierManagement from "./pages/admin/SupplierManagement";
import { ItemsPage, UomPage, LicenseTypesPage, LoadmenPage, StaffPage, WaitingRulesPage, PickupUsersPage, PickupDropTimingsPage, TranslationsPage } from "./pages/admin/MasterPages";
import TripMonitoring from "./pages/admin/TripMonitoring";
import { AttendanceReport, BreakApprovals, FuelReport, GeofenceManagement, ReportsPage } from "./pages/admin/AdminReports";
import LiveTrackingPage from "./pages/admin/LiveTrackingPage";
import ScorecardPage from "./pages/admin/ScorecardPage";
import AdminUsers from "./pages/admin/AdminUsers";
import SupplierCoordHealth from "./pages/admin/SupplierCoordHealth";
import FuelEfficiency from "./pages/admin/FuelEfficiency";
import LoadmanAttendance from "./pages/admin/LoadmanAttendance";
import LoadmenUsage from "./pages/admin/LoadmenUsage";
import HaltLocationMaster from "./pages/admin/HaltLocationMaster";
import FuelSuppliers from "./pages/admin/FuelSuppliers";
import ContractorHelperReport from "./pages/admin/ContractorHelperReport";
import HelperContractors from "./pages/admin/HelperContractors";
import AdminSettings from "./pages/admin/AdminSettings";
import GoogleApiSettings from "./pages/admin/GoogleApiSettings";
import VehicleCapacityPage from "./pages/admin/VehicleCapacityPage";
import AppDistributionPage from "./pages/admin/AppDistributionPage";
import PilotTripsOverview from "./pages/admin/PilotTripsOverview";
import VehicleControl from "./pages/admin/VehicleControl";
import DailyReport from "./pages/admin/DailyReport";

import PilotLayout from "./pages/pilot/PilotLayout";
import PilotDashboard from "./pages/pilot/PilotDashboard";
import PilotTrips from "./pages/pilot/PilotTrips";
import PilotFuel from "./pages/pilot/PilotFuel";
import PilotLiveRoute from "./pages/pilot/PilotLiveRoute";

// Phase 76q — public trip-track page (linked from WhatsApp completion msg).
import PublicTripTrack from "./pages/PublicTripTrack";

import BookingLayout from "./pages/booking/BookingLayout";
import BookingHome from "./pages/booking/BookingHome";
import BookingTrips from "./pages/booking/BookingTrips";
import NewBooking from "./pages/booking/NewBooking";
import PickupDashboard from "./pages/pickup/PickupDashboard";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7]"><div className="text-[#666] text-sm">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/login" replace />;
  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7]"><div className="text-[#666] text-sm">Loading...</div></div>;

  return (
    <Routes>
      <Route path="/upgrade" element={<UpgradeRequired />} />
      {/* Phase 76q — public trip-track page. Unauthenticated. */}
      <Route path="/track/:tripId" element={<PublicTripTrack />} />
      <Route path="/login" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : user.role === 'pilot' ? '/pilot' : user.role === 'pickup' ? '/pickup' : '/booking'} replace /> : <LoginPage />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['admin']}><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="pilots" element={<PilotManagement />} />
        <Route path="vehicles" element={<VehicleManagement />} />
        <Route path="suppliers" element={<SupplierManagement />} />
        <Route path="pickup-users" element={<PickupUsersPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="items" element={<ItemsPage />} />
        <Route path="uom" element={<UomPage />} />
        <Route path="license-types" element={<LicenseTypesPage />} />
        <Route path="loadmen" element={<LoadmenPage />} />
        <Route path="loadman-attendance" element={<LoadmanAttendance />} />
        <Route path="loadmen-usage" element={<LoadmenUsage />} />
        <Route path="trips" element={<TripMonitoring />} />
        <Route path="pilot-trips" element={<PilotTripsOverview />} />
        <Route path="vehicle-control" element={<VehicleControl />} />
        <Route path="attendance" element={<AttendanceReport />} />
        <Route path="breaks" element={<BreakApprovals />} />
        <Route path="fuel" element={<FuelReport />} />
        <Route path="fuel-efficiency" element={<FuelEfficiency />} />
        <Route path="waiting-rules" element={<WaitingRulesPage />} />
        <Route path="pickup-drop-timings" element={<PickupDropTimingsPage />} />
        <Route path="translations" element={<TranslationsPage />} />
        <Route path="geofences" element={<GeofenceManagement />} />
        <Route path="halt-locations" element={<HaltLocationMaster />} />
        <Route path="fuel-suppliers" element={<FuelSuppliers />} />
        <Route path="contractor-helper-report" element={<ContractorHelperReport />} />
        <Route path="helper-contractors" element={<HelperContractors />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="vehicle-capacity" element={<VehicleCapacityPage />} />
        <Route path="app-distribution" element={<AppDistributionPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="daily-report" element={<DailyReport />} />
        <Route path="live-map" element={<LiveTrackingPage />} />
        <Route path="scorecard" element={<ScorecardPage />} />
        <Route path="admin-users" element={<AdminUsers />} />
        <Route path="supplier-coord-health" element={<SupplierCoordHealth />} />
        <Route path="google-api" element={<GoogleApiSettings />} />
      </Route>

      {/* Pilot Routes */}
      <Route path="/pilot" element={<ProtectedRoute roles={['pilot']}><PilotLayout /></ProtectedRoute>}>
        <Route index element={<PilotDashboard />} />
        <Route path="trips" element={<PilotTrips />} />
        <Route path="route" element={<PilotLiveRoute />} />
        <Route path="fuel" element={<PilotFuel />} />
      </Route>

      {/* Booking Staff Routes */}
      <Route path="/booking" element={<ProtectedRoute roles={['booking_staff', 'admin']}><BookingLayout /></ProtectedRoute>}>
        <Route index element={<BookingHome />} />
        <Route path="trips" element={<BookingTrips />} />
        <Route path="new" element={<NewBooking />} />
      </Route>

      {/* Pickup Routes */}
      <Route path="/pickup" element={<ProtectedRoute roles={['pickup', 'admin']}><PickupDashboard /></ProtectedRoute>} />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppUpdateBanner />
        <AppRoutes />
        <Toaster />
        <InstallPWA />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
